import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useAuth } from '../context/AuthContext';
import {
  Shield,
  Award,
  Users,
  CheckCircle,
  AlertTriangle,
  Droplet,
  Heart,
  ArrowRight,
  ChevronDown,
  Phone,
  MessageCircle,
  Download,
  FileText,
  Loader2,
  Edit2,
  Target,
  BookOpen,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  shield: Shield,
  award: Award,
  users: Users,
  check: CheckCircle,
  alert: AlertTriangle,
  droplet: Droplet,
  heart: Heart,
  file: FileText,
  target: Target,
  book: BookOpen,
};

const SafetyPage = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const { isAuthenticated, isAdmin } = useAuth();
  const isAdminUser = isAuthenticated && isAdmin && isAdmin();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/safety-standards-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load safety config:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    // Already a full URL
    if (url.startsWith('http')) return url;
    // Build full URL using window.location.origin for proper browser navigation
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const c = config || {};
  const heroTitle = c.hero_title || 'Safety & Standards';
  const heroSubtitle = c.hero_subtitle || "Your child's safety is our highest priority.";
  const trustBadges = c.trust_badges || [];
  const standardsCards = c.standards_cards || [];
  const milestonesTitle = c.milestones_title || 'Skill Progression Milestones';
  const milestonesSubtitle = c.milestones_subtitle || 'Clear benchmarks your child will achieve';
  const milestones = c.milestones || [];
  const faqTitle = c.faq_title || 'Safety FAQ';
  const faqSubtitle = c.faq_subtitle || 'Common questions from parents';
  const faqs = c.faqs || [];
  const ctaTitle = c.cta_title || 'Questions About Our Safety Standards?';
  const ctaSubtitle = c.cta_subtitle || 'Speak directly with our team';
  const ctaPhone = c.cta_phone || '+268 7600 1234';
  const ctaWhatsapp = c.cta_whatsapp || '+26876001234';
  const ctaEnrollText = c.cta_enroll_text || 'Enroll Now';
  const ctaContactText = c.cta_contact_text || 'Speak to a Coach';
  const safetyPdfUrl = getFileUrl(c.safety_policy_pdf_url);
  const eapPdfUrl = getFileUrl(c.eap_pdf_url);
  const safeguardingPdfUrl = getFileUrl(c.safeguarding_pdf_url);

  const hasDownloads = safetyPdfUrl || eapPdfUrl || safeguardingPdfUrl;

  return (
    <div className="min-h-screen bg-white" data-testid="safety-page">
      {/* Admin Edit Button */}
      {isAdminUser && (
        <Link
          to="/admin/safety-standards"
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-lg hover:shadow-xl hover:border-primary transition-all text-sm font-medium text-slate-700 hover:text-primary"
          data-testid="edit-safety-btn"
        >
          <Edit2 className="w-4 h-4" />
          Edit Safety
        </Link>
      )}

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDJ2LTJoMzR6bTAtNHYySDJ2LTJoMzR6bTAtNHYySDJ2LTJoMzR6bTAtNHYySDJ2LTJoMzR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Back to Home
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Safety First</Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {heroTitle}
            </h1>
            <p className="text-xl text-slate-300 mb-10">
              {heroSubtitle}
            </p>

            {/* Trust Badges */}
            {trustBadges.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {trustBadges.map((badge, idx) => {
                  const IconComponent = ICON_MAP[badge.icon] || Shield;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm"
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-emerald-400" />
                      </div>
                      <span className="text-sm font-medium text-white text-left">{badge.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Standards Cards Section */}
      {standardsCards.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Our Standards</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What We Commit To
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {standardsCards.map((card) => {
                const IconComponent = ICON_MAP[card.icon] || Shield;
                return (
                  <Card key={card.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <IconComponent className="w-7 h-7 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">{card.title}</h3>
                      </div>
                      <ul className="space-y-3">
                        {card.bullets?.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-600">
                            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Skill Progression Milestones */}
      {milestones.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">Progress Tracking</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {milestonesTitle}
              </h2>
              <p className="text-lg text-slate-600">{milestonesSubtitle}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="space-y-4">
                {milestones.map((milestone, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 py-3 px-4 bg-slate-50 rounded-lg">
                      <span className="text-slate-700">{milestone}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">FAQ</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {faqTitle}
              </h2>
              <p className="text-lg text-slate-600">{faqSubtitle}</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, idx) => (
                <AccordionItem
                  key={faq.id || idx}
                  value={faq.id || `faq-${idx}`}
                  className="bg-slate-50 rounded-xl border-0 px-6"
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Downloads Section */}
      {hasDownloads && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-slate-200 text-slate-700 border-slate-300">Resources</Badge>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Download Our Safety Documents
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {safetyPdfUrl && (
                <a
                  href={safetyPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 group-hover:text-primary">Safety Policy</p>
                    <p className="text-sm text-slate-500">PDF Document</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-primary" />
                </a>
              )}

              {eapPdfUrl && (
                <a
                  href={eapPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 group-hover:text-primary">Emergency Plan</p>
                    <p className="text-sm text-slate-500">EAP Summary</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-primary" />
                </a>
              )}

              {safeguardingPdfUrl && (
                <a
                  href={safeguardingPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 group-hover:text-primary">Safeguarding</p>
                    <p className="text-sm text-slate-500">Policy Document</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-primary" />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 via-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-white text-primary border-primary/20 shadow-sm">Get In Touch</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {ctaTitle}
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {ctaSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/#programs">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold px-10 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all group"
              >
                {ctaEnrollText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href={`https://wa.me/${ctaWhatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-10 py-6 text-lg rounded-xl transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {ctaContactText}
              </Button>
            </a>
          </div>

          <div className="flex items-center justify-center gap-2 text-slate-600">
            <Phone className="w-4 h-4" />
            <a href={`tel:${ctaPhone.replace(/\s/g, '')}`} className="hover:text-primary transition-colors">
              {ctaPhone}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-center">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">
            ← Back to Eswatini Seals Swim Academy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default SafetyPage;
